import { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CalendarDays, CheckCircle2, RefreshCw, Loader2, Waves, Clock, ArrowRight, ClipboardCheck } from 'lucide-react';

export function MeetGreetPrompt({ childId, childName, userRole }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [proposedDate, setProposedDate] = useState('');
  const [proposedTime, setProposedTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const [reevalInfo, setReevalInfo] = useState(null);
  const [showReeval, setShowReeval] = useState(false);
  const [requestType, setRequestType] = useState('meet_and_greet');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get(`/meet-greet/status/${childId}`);
      setStatus(res.data);
      if (userRole === 'parent') {
        if (res.data.status === 'none') {
          setOpen(true);
        } else if ((res.data.status === 'pending' || res.data.status === 'countered' || res.data.status === 'instructor_proposed') && res.data.proposed_by !== userRole) {
          setOpen(true);
        }
      }
      // Also check re-evaluation eligibility for parents
      if (userRole === 'parent') {
        try {
          const reevalRes = await api.get(`/meet-greet/can-request-reeval/${childId}`);
          setReevalInfo(reevalRes.data);
        } catch { /* silent */ }
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); }
    finally { setLoading(false); }
  }, [childId, userRole]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handlePropose = async () => {
    if (!proposedDate) { toast.error('Please select a date'); return; }
    setSubmitting(true);
    try {
      await api.post('/meet-greet/request', {
        child_id: childId,
        proposed_date: proposedDate,
        proposed_time: proposedTime,
        meeting_type: requestType,
      });
      const label = requestType === 're_evaluation' ? 'Re-evaluation' : 'Meet & Greet';
      toast.success(`${label} request sent!`);
      setOpen(false);
      setShowReeval(false);
      fetchStatus();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send request'); }
    finally { setSubmitting(false); }
  };

  const handleApprove = async () => {
    if (!status) return;
    setSubmitting(true);
    const mgId = status.meet_greet_id || status.schedule_id;
    try {
      await api.put(`/meet-greet/${mgId}/respond`, { action: 'approve' });
      toast.success('Meeting confirmed!');
      setOpen(false);
      fetchStatus();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to confirm'); }
    finally { setSubmitting(false); }
  };

  const handleSuggest = async () => {
    if (!proposedDate) { toast.error('Please select a new date'); return; }
    setSubmitting(true);
    const mgId = status.meet_greet_id || status.schedule_id;
    try {
      await api.put(`/meet-greet/${mgId}/respond`, {
        action: 'suggest',
        proposed_date: proposedDate,
        proposed_time: proposedTime,
      });
      toast.success('New date suggested!');
      setOpen(false);
      fetchStatus();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to suggest'); }
    finally { setSubmitting(false); }
  };

  if (loading || !status) return null;

  const meetingLabel = status.meeting_type === 're_evaluation' ? 'Re-evaluation' : 'Meet & Greet';
  const isConfirmed = status.status === 'confirmed';
  const isWaitingOnMe = status.status !== 'none' && !isConfirmed && status.proposed_by !== userRole;
  const isWaitingOnOther = status.status !== 'none' && !isConfirmed && status.proposed_by === userRole;

  // Instructors don't see prompt if no negotiation in progress
  if (userRole === 'instructor' && status.status === 'none') return null;

  const formatDate = (d) => {
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  // Show re-evaluation button for parents at milestones
  const canRequestReeval = userRole === 'parent' && reevalInfo?.can_request && (isConfirmed || status.status === 'none');

  return (
    <>
      {/* Active negotiation banner */}
      {!isConfirmed && status.status !== 'none' && !open && (
        <div
          className={`rounded-2xl p-4 mb-6 cursor-pointer transition-all hover:shadow-md ${isWaitingOnMe ? 'bg-[#FFF7ED] border-2 border-amber-300 animate-pulse' : 'bg-[#ECFEFF] border border-[#22D3EE]/30'}`}
          onClick={() => setOpen(true)}
          data-testid="meet-greet-banner"
        >
          <div className="flex items-center gap-3">
            <CalendarDays className={`w-6 h-6 shrink-0 ${isWaitingOnMe ? 'text-amber-500' : 'text-[#22D3EE]'}`} />
            <div className="flex-1">
              <p className="font-bold text-slate-800 text-sm">
                {isWaitingOnMe
                  ? `Action needed: ${meetingLabel} proposed for ${formatDate(status.proposed_date)}`
                  : `Waiting for ${userRole === 'parent' ? 'instructor' : 'parent'} to respond to your ${meetingLabel} proposal`
                }
              </p>
              {status.proposed_time && <p className="text-xs text-slate-500 mt-0.5">Time: {status.proposed_time}</p>}
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      )}

      {/* Re-evaluation request button for parents */}
      {canRequestReeval && (
        <div
          className="rounded-2xl p-4 mb-6 cursor-pointer transition-all hover:shadow-md bg-violet-50 border border-violet-200"
          onClick={() => { setRequestType('re_evaluation'); setShowReeval(true); setProposedDate(''); setProposedTime(''); }}
          data-testid="request-reeval-banner"
        >
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6 text-violet-500 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-slate-800 text-sm">Request a Re-evaluation Session</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {childName} has completed {reevalInfo.session_count} sessions — milestone {reevalInfo.latest_milestone} reached
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-violet-400" />
          </div>
        </div>
      )}

      {/* Rebook cancelled M&G */}
      {userRole === 'parent' && reevalInfo?.can_rebook_mg && status.status === 'none' && !open && (
        <div
          className="rounded-2xl p-4 mb-6 cursor-pointer transition-all hover:shadow-md bg-amber-50 border border-amber-200"
          onClick={() => { setRequestType('meet_and_greet'); setOpen(true); setProposedDate(''); setProposedTime(''); }}
          data-testid="rebook-mg-banner"
        >
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6 text-amber-500 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-slate-800 text-sm">Rebook Meet & Greet</p>
              <p className="text-xs text-slate-500 mt-0.5">Your previous Meet & Greet was cancelled. Tap to schedule a new one.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-amber-400" />
          </div>
        </div>
      )}

      {/* Re-evaluation Dialog */}
      <Dialog open={showReeval} onOpenChange={setShowReeval}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2 text-lg">
              <ClipboardCheck className="w-6 h-6 text-violet-500" /> Re-evaluation Session
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-1">
            <div className="bg-violet-50 rounded-2xl p-4 text-center">
              <p className="text-sm text-slate-600">
                Schedule a <strong>Re-evaluation Session</strong> for <strong>{childName}</strong> to review progress, goals, and the plan going forward.
              </p>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Preferred Date</label>
              <Input type="date" value={proposedDate} onChange={e => setProposedDate(e.target.value)} className="rounded-xl mt-1" data-testid="reeval-date-input" min={new Date().toISOString().split('T')[0]} />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Preferred Time (optional)</label>
              <Input type="time" value={proposedTime} onChange={e => setProposedTime(e.target.value)} className="rounded-xl mt-1" data-testid="reeval-time-input" />
            </div>
            <Button onClick={handlePropose} disabled={submitting} className="w-full rounded-full bg-violet-600 hover:bg-violet-700 text-white font-bold h-11 gap-2" data-testid="reeval-propose-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
              {submitting ? 'Sending...' : 'Request Re-evaluation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meet & Greet Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2 text-lg">
              <Waves className="w-6 h-6 text-[#22D3EE]" /> {meetingLabel}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-1">
            {/* No M&G exists — parent proposes */}
            {status.status === 'none' && (
              <>
                <div className="bg-[#ECFEFF]/60 rounded-2xl p-4 text-center">
                  <p className="text-sm text-slate-600">
                    {requestType === 're_evaluation'
                      ? <>Schedule a <strong>Re-evaluation</strong> for <strong>{childName}</strong>.</>
                      : <>Welcome! Let's schedule a <strong>Meet & Greet</strong> for <strong>{childName}</strong>. Pick a date and time that works for you.</>
                    }
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-600">Preferred Date</label>
                  <Input type="date" value={proposedDate} onChange={e => setProposedDate(e.target.value)} className="rounded-xl mt-1" data-testid="mg-date-input" min={new Date().toISOString().split('T')[0]} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-600">Preferred Time (optional)</label>
                  <Input type="time" value={proposedTime} onChange={e => setProposedTime(e.target.value)} className="rounded-xl mt-1" data-testid="mg-time-input" />
                </div>
                <Button onClick={handlePropose} disabled={submitting} className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold h-11 gap-2" data-testid="mg-propose-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
                  {submitting ? 'Sending...' : `Propose ${requestType === 're_evaluation' ? 'Re-evaluation' : 'Meet & Greet'}`}
                </Button>
              </>
            )}

            {/* A date was proposed — waiting for my response */}
            {isWaitingOnMe && (
              <>
                <div className="bg-amber-50 rounded-2xl p-4 text-center border border-amber-200">
                  <p className="text-sm text-slate-700">
                    <strong>{status.proposed_by_name || (status.proposed_by === 'instructor' ? (status.instructor_name || 'Your instructor') : 'Parent')}</strong> has proposed a {meetingLabel}:
                  </p>
                  <div className="mt-3 flex items-center justify-center gap-3">
                    <Badge className="rounded-full bg-[#1E1B4B] text-white text-sm px-4 py-1.5 gap-1.5">
                      <CalendarDays className="w-4 h-4" /> {formatDate(status.proposed_date)}
                    </Badge>
                    {status.proposed_time && (
                      <Badge className="rounded-full bg-slate-700 text-white text-sm px-4 py-1.5 gap-1.5">
                        <Clock className="w-4 h-4" /> {status.proposed_time}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={handleApprove} disabled={submitting} className="rounded-full bg-green-500 hover:bg-green-600 text-white font-bold gap-2" data-testid="mg-approve-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Approve
                  </Button>
                  <Button variant="outline" onClick={() => setShowSuggestForm(true)} className="rounded-full font-bold gap-2 border-amber-300 text-amber-700" data-testid="mg-suggest-toggle">
                    <RefreshCw className="w-4 h-4" /> Suggest Different
                  </Button>
                </div>

                {showSuggestForm && (
                  <div className="space-y-3 border-t pt-3">
                    <p className="text-xs font-bold text-slate-500 uppercase">Suggest a new date</p>
                    <Input type="date" value={proposedDate} onChange={e => setProposedDate(e.target.value)} className="rounded-xl" data-testid="mg-new-date" min={new Date().toISOString().split('T')[0]} />
                    <Input type="time" value={proposedTime} onChange={e => setProposedTime(e.target.value)} className="rounded-xl" data-testid="mg-new-time" />
                    <Button onClick={handleSuggest} disabled={submitting || !proposedDate} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold gap-2" data-testid="mg-suggest-btn">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Suggest This Date
                    </Button>
                  </div>
                )}

                {status.history && status.history.length > 1 && (
                  <div className="border-t pt-3">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-2">Negotiation History</p>
                    {status.history.map((h, i) => (
                      <div key={`history-${i}`} className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                        <span className="font-semibold capitalize">{h.proposed_by_name || h.proposed_by}</span>
                        <span>proposed {h.date}{h.time ? ` at ${h.time}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Waiting on the other person */}
            {isWaitingOnOther && (
              <div className="bg-[#ECFEFF]/60 rounded-2xl p-4 text-center">
                <Loader2 className="w-8 h-8 text-[#22D3EE] mx-auto mb-3 animate-spin" />
                <p className="text-sm text-slate-600">
                  Waiting for <strong>{userRole === 'parent' ? 'your instructor' : 'the parent'}</strong> to respond to your proposed date:
                </p>
                <div className="mt-3">
                  <Badge className="rounded-full bg-[#1E1B4B] text-white text-sm px-4 py-1.5 gap-1.5">
                    <CalendarDays className="w-4 h-4" /> {formatDate(status.proposed_date)}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
